/**
 * CalendarPage — a unified month-grid farm calendar (§60). Overlays, by date:
 * recommended care due, overdue care, tasks, harvests logged, and spoilage events —
 * all from existing endpoints (no backend). Complements the subscribable .ics feed
 * (Settings → Notifications). Click a day to see everything on it.
 */
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'
import { careApi, tasksApi, harvestsApi, spoilageApi, type CareEvent, type HarvestEvent, type SpoilageEvent } from '../lib/api'

type Kind = 'care' | 'overdue' | 'task' | 'harvest' | 'spoilage'
interface CalEvent { date: string; kind: Kind; label: string }
const KIND: Record<Kind, { label: string; color: string }> = {
  care:     { label: 'Care due',  color: '#F5A623' },
  overdue:  { label: 'Overdue',   color: '#dc2626' },
  task:     { label: 'Task',      color: '#38bdf8' },
  harvest:  { label: 'Harvest',   color: '#2D6A2F' },
  spoilage: { label: 'Spoilage',  color: '#9333ea' },
}
const ymd = (d: unknown) => String(d ?? '').slice(0, 10)
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function CalendarPage() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })
  const [selected, setSelected] = useState<string | null>(null)

  const care = useQuery({ queryKey: ['care-upcoming', 120], queryFn: () => careApi.upcoming(120).then(r => r.data as CareEvent[]) })
  const overdue = useQuery({ queryKey: ['care-overdue'], queryFn: () => careApi.overdue().then(r => r.data as CareEvent[]) })
  const tasks = useQuery({ queryKey: ['tasks-all'], queryFn: () => tasksApi.list().then(r => (r.data as { tasks?: unknown[] }).tasks ?? []) })
  const harvests = useQuery({ queryKey: ['harvests'], queryFn: () => harvestsApi.list().then(r => r.data.harvests) })
  const spoilage = useQuery({ queryKey: ['spoilage'], queryFn: () => spoilageApi.list().then(r => r.data.events) })

  const events = useMemo(() => {
    const out: CalEvent[] = []
    for (const c of care.data ?? []) if (c.due_date) out.push({ date: ymd(c.due_date), kind: 'care', label: `${c.action}${c.plant_label ? `: ${c.plant_label}` : ''}` })
    for (const c of overdue.data ?? []) if (c.due_date) out.push({ date: ymd(c.due_date), kind: 'overdue', label: `${c.action}${c.plant_label ? `: ${c.plant_label}` : ''}` })
    for (const t of (tasks.data ?? []) as Array<Record<string, unknown>>) {
      const due = t.due_date as string | undefined
      if (due && t.status !== 'done' && t.status !== 'cancelled') out.push({ date: ymd(due), kind: 'task', label: String(t.title ?? 'Task') })
    }
    for (const h of (harvests.data ?? []) as HarvestEvent[]) out.push({ date: ymd(h.harvested_at), kind: 'harvest', label: `${h.variety_name || 'Harvest'}${h.weight_kg ? ` · ${h.weight_kg}kg` : ''}` })
    for (const s of (spoilage.data ?? []) as SpoilageEvent[]) out.push({ date: ymd(s.occurred_at), kind: 'spoilage', label: `Loss${s.variety_name ? `: ${s.variety_name}` : ''}` })
    return out
  }, [care.data, overdue.data, tasks.data, harvests.data, spoilage.data])

  const byDate = useMemo(() => {
    const m = new Map<string, CalEvent[]>()
    for (const e of events) { const a = m.get(e.date) ?? []; a.push(e); m.set(e.date, a) }
    return m
  }, [events])

  // Build the grid: leading blanks for the first weekday, then the month's days.
  const y = month.getFullYear(), mo = month.getMonth()
  const firstWeekday = new Date(y, mo, 1).getDay()
  const daysInMonth = new Date(y, mo + 1, 0).getDate()
  const cells: (string | null)[] = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${y}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
  const todayStr = ymd(new Date().toISOString())
  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

  const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12 }
  const navBtn: React.CSSProperties = { background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 8, cursor: 'pointer', color: 'var(--color-text)', padding: 6, display: 'flex', alignItems: 'center' }

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarDays size={20} /> Farm calendar
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setMonth(new Date(y, mo - 1, 1))} style={navBtn} aria-label="Previous month"><ChevronLeft size={16} /></button>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', minWidth: 130, textAlign: 'center' }}>{monthLabel}</span>
          <button onClick={() => setMonth(new Date(y, mo + 1, 1))} style={navBtn} aria-label="Next month"><ChevronRight size={16} /></button>
          <button onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)) }} style={{ ...navBtn, padding: '6px 10px', fontSize: 12.5 }}>Today</button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        {(Object.keys(KIND) as Kind[]).map(k => (
          <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-muted)' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: KIND[k].color }} /> {KIND[k].label}
          </span>
        ))}
      </div>

      {/* Month grid */}
      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WD.map(w => <div key={w} style={{ padding: '6px 4px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)' }}>{w}</div>)}
          {cells.map((day, i) => {
            const evs = day ? (byDate.get(day) ?? []) : []
            const isToday = day === todayStr
            return (
              <div key={i} onClick={() => day && evs.length && setSelected(day)}
                style={{ minHeight: 78, borderRight: (i % 7 !== 6) ? '1px solid var(--color-border)' : 'none', borderBottom: '1px solid var(--color-border)', padding: 4, background: day ? (isToday ? 'color-mix(in srgb, var(--color-primary) 8%, transparent)' : 'transparent') : 'var(--color-bg)', cursor: evs.length ? 'pointer' : 'default', verticalAlign: 'top' }}>
                {day && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--color-primary)' : 'var(--color-text-muted)', marginBottom: 2 }}>{parseInt(day.slice(8), 10)}</div>
                    {evs.slice(0, 3).map((e, j) => (
                      <div key={j} title={e.label} style={{ fontSize: 10, lineHeight: 1.3, color: '#fff', background: KIND[e.kind].color, borderRadius: 4, padding: '1px 4px', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.label}</div>
                    ))}
                    {evs.length > 3 && <div style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>+{evs.length - 3} more</div>}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selected && (
        <div style={{ ...card, marginTop: 14, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{new Date(selected + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 12 }}>Close</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(byDate.get(selected) ?? []).map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: KIND[e.kind].color, flexShrink: 0 }} />
                <span style={{ color: 'var(--color-text-muted)', fontSize: 11, minWidth: 56 }}>{KIND[e.kind].label}</span>
                <span>{e.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  )
}

export default CalendarPage
